
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { BackgroundEffects } from "@/components/landing/index/BackgroundEffects";
import { ScrollToTopButton } from "@/components/landing/index/ScrollToTopButton";
import { MainContent } from "@/components/landing/index/MainContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { SEOHead } from "@/components/SEO/SEOHead";
import { useUserStore } from "@/store/userStore";

const Index = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useUserStore();
  const navigate = useNavigate();

  // Welcome toast
  useEffect(() => {
    toast({
      title: "Welcome to DGK Platform! 🚀",
      description: "Explore our innovative business solutions.",
      duration: 5000,
    });
  }, [toast]);

  // Smooth scroll effect for navigation
  useEffect(() => {
    // Handle smooth scrolling for anchor links
    const smoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if ((e.target as HTMLAnchorElement).hash) {
        e.preventDefault();
        const hash = (e.target as HTMLAnchorElement).hash;
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    // Toggle visibility of scroll-to-top button
    const toggleScrollButton = () => {
      const scrollButton = document.getElementById('scroll-to-top');
      if (scrollButton) {
        if (window.scrollY > 500) {
          scrollButton.classList.remove('opacity-0', 'pointer-events-none');
          scrollButton.classList.add('opacity-100', 'pointer-events-auto');
        } else {
          scrollButton.classList.add('opacity-0', 'pointer-events-none');
          scrollButton.classList.remove('opacity-100', 'pointer-events-auto');
        }
      }
    };

    // Add event listeners
    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach(anchor => {
      anchor.addEventListener('click', smoothScroll as unknown as EventListener);
    });

    window.addEventListener('scroll', toggleScrollButton);

    // Cleanup function
    return () => {
      anchors.forEach(anchor => {
        anchor.removeEventListener('click', smoothScroll as unknown as EventListener);
      });
      window.removeEventListener('scroll', toggleScrollButton);
    };
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <>
      <SEOHead
        title="DGK Freelance Hub - Blockchain-Powered Freelancing Platform"
        description="Find top freelance talent or projects with our AI-powered matching system. Secure payments, verified skills, and seamless collaboration - all powered by blockchain technology."
        canonicalUrl="/"
      >
        <meta name="keywords" content="freelance,blockchain,job matching,remote work,digital marketplace,secure payments,talent platform" />
        <meta name="robots" content="index, follow" />
      </SEOHead>

      <div className="relative overflow-hidden w-full min-h-screen">
        {/* Background effects */}
        <BackgroundEffects />

        {/* Main content */}
        <div className="relative z-10 w-full">
          <MainContent />
        </div>

        {/* UI controls */}
        <ScrollToTopButton />
      </div>
    </>
  );
};

export default Index;
